import React from 'react';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { ApolloProvider } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import gql from 'graphql-tag';
import JssProvider from 'react-jss/lib/JssProvider';
import { createGenerateClassName, jssPreset } from '@material-ui/core/styles';
import { create } from 'jss';
import { SnackbarProvider } from 'notistack';
import CssBaseline from '@material-ui/core/CssBaseline';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import client from '../lib/apolloClient';
import configureStore from '../store/findBobPublicStore';
import themes from '../lib/themes';
import SuccessionValuation from '../components/Succession/SuccessionValuation';
import SuccessionThemeProvider from '../components/Succession/SuccessionThemeProvider';
import getMessages from '../lib/i18n/getMessages';
import { CurrencyProvider } from '../hooks/useCurrency';

// Define the GraphQL query
export const SUCCESSION_VALUATION_QUERY = gql`
  query SuccessionValuationQuery($input: Input) {
    successionValuationData(input: $input) {
      id
      valueOfPractice
      sumDiscountedCashFlows
      discountedTerminalValue
      year1Valuation
      year1AnnualRevenue
      year1RecurringRevenue
      year3Valuation
      year3AnnualRevenue
      year3RecurringRevenue
      year5Valuation
      year5AnnualRevenue
      year5RecurringRevenue
      year0GrossProduction
      year0TargetPracticeNetRevenue
      year0DirectExpenses
      year0OverheadExpenses
      year0Ebit
      year0Taxes
      year0Fcf
      year0GridRate
      yearlyData {
        year
        targetPracticeNetRevenue
        growthYoy
        directExpenses
        overheadExpenses
        ebit
        taxes
        fcf
        discountRate
        discountedCashFlow
        mtr
      }
      changeInValuation
      changeInValuationPercentage
      percentile
      valuation_last_accessed_date
      user {
        id
        name
        user_name
      }
    }
  }
`;

const SuccessionValuationContainer = (props, railsContext) => {
  // Transform props to match reducer structure
  const initialState = {
    company: props.company || { 
      host: railsContext.company_host,
      features: railsContext.features,
      file_import_disclaimer: railsContext.file_import_disclaimer,
      name: railsContext.company_name,
      branded_name: railsContext.company_branded_name,
      short_name: railsContext.company_short_name,
    },
    user: props.current_user,
    intl: {
      locale: railsContext.i18nLocale || 'en'
    }
  };

  const store = configureStore(initialState);

  const generateClassName = createGenerateClassName({
    dangerouslyUseGlobalCSS: true,
    productionPrefix: 'fbSuccession',
  });

  const jss = create({
    ...jssPreset(),
  });

  const theme = themes[railsContext.theme_name];
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
  window.currency = (railsContext && railsContext.currency) || window.currency || 'USD';

  // Get input parameters from URL query string if present
  const getInputFromQueryParams = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const userId = urlParams.get('userId');
      
      if (userId) {
        // GraphQL Input type uses camelCase
        const input = { userId: userId };
        console.log('Using input parameters:', input);
        return input;
      }
    }
    return null;
  };

  // Build input from Rails-provided dcf_params when present
  const getInputFromDcfParams = () => {
    const p = props?.dcf_params;
    if (!p || !p.dcf_id) return null;

    const toNumber = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };

    // GraphQL Input type fields are camelCase; convert and scale where needed
    const input = {
      useBenchmarkExpensePercentage: Boolean(p.use_benchmark_expense_percentage),
      year1Gross: toNumber(p.year_1_gross),
      year2Gross: toNumber(p.year_2_gross),
      year3Gross: toNumber(p.year_3_gross),
      year4Gross: toNumber(p.year_4_gross),
      year5Gross: toNumber(p.year_5_gross),
      feeBasedRevenue: toNumber(p.fee_based_revenue),
      trailerRevenue: toNumber(p.trailer_revenue),
      transactionalRevenue: toNumber(p.transactional_revenue),
      directRevenue: toNumber(p.direct_revenue),
      grossExpenses: toNumber(p.gross_expenses),
      // GraphQL input expects percentages; Rails sent decimals
      expenseGrowthRate: toNumber(p.expense_growth_rate) * 100,
      useRevenueGrowthRate: Boolean(p.use_revenue_growth_rate),
      revenueGrowthRate: toNumber(p.revenue_growth_rate) * 100,
      branchNetPayout: toNumber(p.branch_net_payout) * 100,
    };

    console.log('Using DCF params for initial input:', input);
    return input;
  };

  const buildInitialInput = () => {
    // Prefer DCF params if present
    const dcfInput = getInputFromDcfParams();
    if (dcfInput) return dcfInput;
    // Fallback to URL-based input
    const urlInput = getInputFromQueryParams();
    if (urlInput) return urlInput;
    return null;
  };

  const locale = window.locale;
  return (
    <CurrencyProvider currency={(railsContext && railsContext.currency) || window.currency || 'USD'}>
    <IntlProvider locale={locale} messages={getMessages(locale)}>
    <JssProvider jss={jss} generateClassName={generateClassName} classNamePrefix="fbSuccession-">
      <MuiThemeProvider theme={createMuiTheme(theme)}>
        <CssBaseline />
        <Provider store={store}>
          <SnackbarProvider maxSnack={5}>
            <ApolloProvider client={client}>
              <SuccessionThemeProvider
                company={initialState.company}
                user={props.current_user}
              >
                <Query
                  query={SUCCESSION_VALUATION_QUERY}
                  variables={{ input: buildInitialInput() }}
                  fetchPolicy="network-only"
                >
                  {({ loading, error, data }) => {
                    return (
                      <SuccessionValuation
                        loading={loading}
                        error={error}
                        valuationData={data?.successionValuationData}
                        {...props}
                      />
                    );
                  }}
                </Query>
              </SuccessionThemeProvider>
            </ApolloProvider>
          </SnackbarProvider>
        </Provider>
      </MuiThemeProvider>
    </JssProvider>
    </IntlProvider>
    </CurrencyProvider>
  );
};

export default SuccessionValuationContainer; 